import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, Panel, Pill, StatusBadge, Skeleton } from "@/components/Bits";
import { Plus, Sparkle, X, PhoneCall, EnvelopeSimple, ChatTeardropDots, GraduationCap, IdentificationCard, Fire, Snowflake, ThermometerHot, Briefcase, Funnel, MagnifyingGlass, FileArrowDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import PhoneInput from "@/components/PhoneInput";
import { NameInput, NumberInput } from "@/lib/validators";
import { useAuth } from "@/contexts/AuthContext";

const STAGES = [
  { key: "new", label: "New", tone: "lavender" },
  { key: "contacted", label: "Contacted", tone: "mustard" },
  { key: "demo_scheduled", label: "Demo scheduled", tone: "mustard" },
  { key: "visited", label: "Visited", tone: "sage" },
  { key: "converted", label: "Converted", tone: "sage" },
  { key: "lost", label: "Lost", tone: "ink" },
];

const SOURCES = ["", "Walk-in", "Phone", "Social Media", "Website", "Referral",
  "Event", "QR Code", "Google Ads", "Meta Ads", "Other"];
const AGE_GROUPS = ["", "Toddler", "Pre-K", "Nursery", "LKG", "UKG", "Daycare"];
const INTEREST = ["", "preschool", "daycare", "preschool_daycare", "activity_only"];

export default function Enquiries() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [enquiries, setEnquiries] = useState([]);
  const [counsellors, setCounsellors] = useState([]);
  const [selected, setSelected] = useState(null);
  const [aiSuggestion, setAiSuggestion] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [converting, setConverting] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    q: "", source: "", interested_in: "", age_group: "",
    counsellor_id: "", unassigned: false, lead_score_min: "",
    submitted_from: "", submitted_to: "", converted: "",
    follow_up_overdue: false,
  });

  // Build params for the GET /enquiries call.
  const params = useMemo(() => {
    const out = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v === "" || v === false || v === null) return;
      out[k] = v;
    });
    return out;
  }, [filters]);

  const load = async () => {
    const { data } = await api.get("/enquiries", { params });
    setEnquiries(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [params]);

  useEffect(() => {
    // Counsellors list — for the assignment dropdown in the drawer.
    api.get("/users", { params: { role: "counsellor", per_page: 100 } })
      .then(({ data }) => setCounsellors(Array.isArray(data) ? data : (data?.items || [])))
      .catch(() => setCounsellors([]));
  }, []);

  const grouped = useMemo(() => {
    const out = Object.fromEntries(STAGES.map(s => [s.key, []]));
    enquiries.forEach(e => { if (out[e.status]) out[e.status].push(e); });
    return out;
  }, [enquiries]);

  const move = async (id, newStatus) => {
    try {
      await api.patch(`/enquiries/${id}`, { status: newStatus, note: `Moved to ${newStatus}` });
      toast.success("Updated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const askAI = async (e) => {
    setAiLoading(true);
    setAiSuggestion("");
    try {
      const summary = `Parent ${e.parent_name} enquired for ${e.child_name} (${e.age_group}). Status: ${e.status}. Source: ${e.source}. Phone ${e.phone}. Last note: ${e.notes?.slice(-1)[0]?.note || "—"}.`;
      const { data } = await api.post("/ai/enquiry-followup", { summary });
      setAiSuggestion(data.text);
    } catch (err) {
      toast.error("MangoCubs AI couldn't help right now");
    } finally {
      setAiLoading(false);
    }
  };

  const handleConvert = async (e) => {
    if (e.converted_admission_id) {
      navigate(`/app/admissions?id=${e.converted_admission_id}`);
      return;
    }
    setConverting(true);
    try {
      const { data } = await api.post(`/enquiries/${e.enquiry_id}/convert`, {});
      const adm = data?.admission;
      if (adm?.admission_number) toast.success(`Converted · admission ${adm.admission_number}`);
      else toast.success("Converted to admission");
      await load();
      setSelected(null);
      navigate(`/app/admissions`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Conversion failed");
    } finally {
      setConverting(false);
    }
  };

  const assignCounsellor = async (enqId, counsellorId) => {
    try {
      await api.patch(`/enquiries/${enqId}`, { counsellor_id: counsellorId || null });
      toast.success(counsellorId ? "Counsellor assigned" : "Unassigned");
      await load();
      setSelected((s) => s && { ...s, counsellor_id: counsellorId });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not assign");
    }
  };

  const attendMyself = (enq) => assignCounsellor(enq.enquiry_id, user?.user_id);

  const downloadCSV = () => {
    const rows = [
      ["Form #", "Status", "Parent", "Child", "Phone", "Email",
       "Source", "Interested in", "Lead score", "Counsellor", "Created"],
      ...enquiries.map((e) => [
        e.form_number || "", e.status || "",
        e.parent_name || "", e.child_name || "",
        e.phone || "", e.email || "",
        e.source || "", e.interested_in || "",
        e.lead_score || "", e.counsellor_id || "",
        (e.created_at || "").slice(0, 10),
      ]),
    ];
    const csv = rows.map((r) =>
      r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div>
      <PageHeader
        eyebrow="CRM"
        title="Enquiries pipeline"
        subtitle="Drag warmth across stages. Convert without losing the human touch."
        action={
          <div className="flex gap-2">
            <button
              onClick={downloadCSV}
              data-testid="enq-csv-export"
              className="px-4 h-10 rounded-full border border-border text-sm flex items-center gap-2 bg-white hover:bg-paper"
            >
              <FileArrowDown size={16} weight="bold" /> Export
            </button>
            <button
              onClick={() => setFiltersOpen((v) => !v)}
              data-testid="enq-filter-toggle"
              className="px-4 h-10 rounded-full bg-ink text-paper text-sm flex items-center gap-2"
            >
              <Funnel size={16} weight="bold" /> {filtersOpen ? "Hide filters" : "Filters"}
            </button>
            <button onClick={() => setCreating(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-terracotta text-white font-semibold hover:bg-terracotta-hover" data-testid="new-enquiry-btn">
              <Plus weight="bold" /> New enquiry
            </button>
          </div>
        }
      />

      {filtersOpen && (
        <Panel title="Filters" className="mb-6" testid="enq-filters-panel">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <FilterInput label="Search" testid="enq-filter-search" icon={<MagnifyingGlass />}
              value={filters.q} onChange={(v) => setFilters({ ...filters, q: v })}
              placeholder="Form #, parent, child, phone…" />
            <FilterSelect label="Source" testid="enq-filter-source"
              value={filters.source} onChange={(v) => setFilters({ ...filters, source: v })}
              options={SOURCES.map((s) => ({ v: s, l: s || "Any" }))} />
            <FilterSelect label="Interested in" testid="enq-filter-interest"
              value={filters.interested_in}
              onChange={(v) => setFilters({ ...filters, interested_in: v })}
              options={INTEREST.map((s) => ({ v: s, l: s ? s.replace("_", " + ") : "Any" }))} />
            <FilterSelect label="Age group" testid="enq-filter-age"
              value={filters.age_group}
              onChange={(v) => setFilters({ ...filters, age_group: v })}
              options={AGE_GROUPS.map((s) => ({ v: s, l: s || "Any" }))} />
            <FilterSelect label="Counsellor" testid="enq-filter-counsellor"
              value={filters.counsellor_id}
              onChange={(v) => setFilters({ ...filters, counsellor_id: v })}
              options={[
                { v: "", l: "Any" },
                ...counsellors.map((c) => ({ v: c.user_id, l: c.name || c.email })),
              ]} />
            <FilterInput label="Min lead score" type="number" testid="enq-filter-score"
              value={filters.lead_score_min}
              onChange={(v) => setFilters({ ...filters, lead_score_min: v })}
              placeholder="e.g. 70" />
            <FilterInput label="Submitted from" type="date" testid="enq-filter-from"
              value={filters.submitted_from} onChange={(v) => setFilters({ ...filters, submitted_from: v })} />
            <FilterInput label="Submitted to" type="date" testid="enq-filter-to"
              value={filters.submitted_to} onChange={(v) => setFilters({ ...filters, submitted_to: v })} />
            <FilterSelect label="Converted" testid="enq-filter-converted"
              value={filters.converted}
              onChange={(v) => setFilters({ ...filters, converted: v })}
              options={[
                { v: "", l: "Any" },
                { v: "true", l: "Converted" },
                { v: "false", l: "Not converted" },
              ]} />
            <div className="lg:col-span-4 flex flex-wrap gap-3 pt-2 text-sm">
              <label className="inline-flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={filters.unassigned}
                  onChange={(e) => setFilters({ ...filters, unassigned: e.target.checked })}
                  className="w-4 h-4 accent-terracotta" data-testid="enq-filter-unassigned" />
                Unassigned only
              </label>
              <label className="inline-flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={filters.follow_up_overdue}
                  onChange={(e) => setFilters({ ...filters, follow_up_overdue: e.target.checked })}
                  className="w-4 h-4 accent-terracotta" data-testid="enq-filter-followup-overdue" />
                Follow-up overdue
              </label>
            </div>
          </div>
        </Panel>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {STAGES.map(stage => (
          <div key={stage.key} className="bg-white rounded-3xl border border-border p-4 min-h-[200px]" data-testid={`stage-${stage.key}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="font-heading font-semibold text-sm">{stage.label}</div>
              <Pill tone={stage.tone}>{grouped[stage.key].length}</Pill>
            </div>
            <div className="space-y-2">
              {grouped[stage.key].map(e => (
                <button
                  key={e.enquiry_id}
                  onClick={() => setSelected(e)}
                  className="w-full text-left p-3 rounded-2xl bg-paper-alt hover:bg-mustard-soft transition border border-transparent hover:border-mustard"
                  data-testid={`enq-card-${e.enquiry_id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">{e.parent_name}</div>
                      <div className="text-xs text-ink-soft truncate">{e.child_name} · {e.age_group}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {e.lead_score && (
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${e.lead_score >= 80 ? "bg-sage text-white" : e.lead_score >= 65 ? "bg-mustard text-white" : "bg-ink/10 text-ink-soft"}`}>
                          {e.lead_score}
                        </span>
                      )}
                      {e.temperature && (
                        <StatusBadge status={e.temperature} label={e.temperature} />
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-xs text-terracotta">{e.source}</span>
                    {e.follow_up_date && (
                      <FollowUpHint date={e.follow_up_date} />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {selected && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={() => setSelected(null)}>
          <div className="w-full max-w-2xl bg-white rounded-t-3xl sm:rounded-3xl border border-border shadow-floating max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="enquiry-detail">
            <div className="p-6 border-b border-border flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <div className="text-xs uppercase tracking-wider text-ink-soft">Enquiry</div>
                <h3 className="font-heading text-2xl font-bold mt-1">{selected.parent_name}</h3>
                <div className="text-sm text-ink-soft">Child: {selected.child_name} · {selected.age_group}</div>
                {selected.form_number && (
                  <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-paper border border-border text-xs font-mono"
                    data-testid="enquiry-form-number-pill">
                    <IdentificationCard size={14} weight="duotone" /> {selected.form_number}
                  </div>
                )}
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {selected.lead_score && (
                    <Pill tone={selected.lead_score >= 80 ? "sage" : selected.lead_score >= 65 ? "mustard" : "ink"}>
                      Lead score: {selected.lead_score}
                    </Pill>
                  )}
                  {selected.interested_in && <Pill tone="lavender">Wants: {selected.interested_in.replace("_", " + ")}</Pill>}
                  {selected.location && <Pill tone="terracotta">📍 {selected.location}</Pill>}
                  {selected.instagram && <Pill tone="lavender">IG {selected.instagram}</Pill>}
                  {selected.facebook && <Pill tone="lavender">FB {selected.facebook}</Pill>}
                </div>
              </div>
              <button onClick={() => setSelected(null)} className="w-10 h-10 rounded-xl hover:bg-paper-alt flex items-center justify-center" data-testid="close-enquiry-detail">
                <X />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-3 gap-3">
                <a href={`tel:${selected.phone}`} className="p-3 rounded-2xl bg-terracotta-soft text-terracotta flex flex-col items-center"><PhoneCall weight="duotone" /><span className="text-xs mt-1">{selected.phone}</span></a>
                <a href={`mailto:${selected.email || ""}`} className="p-3 rounded-2xl bg-mustard-soft text-mustard-hover flex flex-col items-center"><EnvelopeSimple weight="duotone" /><span className="text-xs mt-1 truncate w-full text-center">{selected.email || "—"}</span></a>
                <button className="p-3 rounded-2xl bg-sage-soft text-sage-deep flex flex-col items-center"><ChatTeardropDots weight="duotone" /><span className="text-xs mt-1">WhatsApp</span></button>
              </div>

              <div>
                <div className="text-sm font-semibold mb-2">Assign to counsellor</div>
                <div className="flex flex-wrap gap-2 items-center" data-testid="assign-block">
                  <select
                    value={selected.counsellor_id || ""}
                    onChange={(e) => assignCounsellor(selected.enquiry_id, e.target.value || null)}
                    className="h-10 px-3 rounded-xl border border-border bg-white text-sm"
                    data-testid="assign-counsellor-select"
                  >
                    <option value="">Unassigned</option>
                    {counsellors.map((c) => (
                      <option key={c.user_id} value={c.user_id}>{c.name || c.email}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => attendMyself(selected)}
                    className="h-10 px-3 rounded-full bg-mustard text-ink text-xs font-semibold hover:bg-mustard-hover hover:text-white"
                    data-testid="attend-myself-btn"
                  >
                    Attend myself
                  </button>
                </div>
              </div>

              <div>
                <div className="text-sm font-semibold mb-2">Move to stage</div>
                <div className="flex flex-wrap gap-2">
                  {STAGES.map(s => (
                    <button
                      key={s.key}
                      onClick={() => move(selected.enquiry_id, s.key)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border ${selected.status === s.key ? "bg-terracotta text-white border-terracotta" : "bg-white border-border hover:bg-paper-alt"}`}
                      data-testid={`move-${s.key}`}
                    >{s.label}</button>
                  ))}
                </div>
              </div>

              <div className="p-4 rounded-2xl bg-sage-soft border border-sage flex items-center justify-between gap-3"
                data-testid="convert-block">
                <div>
                  <div className="font-semibold flex items-center gap-1.5"><GraduationCap weight="duotone" /> Ready for admission?</div>
                  <div className="text-xs text-ink-soft mt-0.5">
                    {selected.converted_admission_id
                      ? "Already converted — open the linked admission."
                      : "Mint an admission number and carry over all details + history."}
                  </div>
                </div>
                <button onClick={() => handleConvert(selected)} disabled={converting}
                  data-testid="convert-to-admission-btn"
                  className="px-4 py-2 rounded-full bg-sage-deep text-white text-sm font-semibold disabled:opacity-60 hover:bg-sage-deep/90 whitespace-nowrap">
                  {selected.converted_admission_id ? "Open admission" : (converting ? "Converting…" : "Convert →")}
                </button>
              </div>

              <div>
                <div className="text-sm font-semibold mb-2">Notes</div>
                <div className="space-y-2">
                  {(selected.notes || []).slice().reverse().map((n, i) => (
                    <div key={i} className="p-3 bg-paper-alt rounded-2xl text-sm">
                      <div className="text-xs text-ink-soft">{new Date(n.ts).toLocaleString()} · {n.by}</div>
                      <div className="mt-1">{n.note}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-4 rounded-2xl bg-ink text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><Sparkle weight="duotone" className="text-mustard" /> <span className="font-semibold">MangoCubs AI</span></div>
                  <button onClick={() => askAI(selected)} disabled={aiLoading} className="text-xs px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 disabled:opacity-50" data-testid="enquiry-ai-suggest">
                    {aiLoading ? "Thinking..." : "Suggest next step"}
                  </button>
                </div>
                {aiSuggestion && <div className="text-sm mt-3 whitespace-pre-wrap leading-relaxed">{aiSuggestion}</div>}
              </div>
            </div>
          </div>
        </div>
      )}

      {creating && <NewEnquiryModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function NewEnquiryModal({ onClose, onCreated }) {
  const { user } = useAuth();
  const [f, setF] = useState({
    parent_name: "", child_name: "", phone: "", email: "",
    age_group: "Nursery", source: "Walk-in",
    branch_id: user?.branch_id || (user?.branch_ids?.[0] ?? ""),
    interested_in: "preschool",
    // Internal CRM fields
    temperature: "warm",
    counsellor_id: "",
    follow_up_date: "",
    admission_probability: 50,
    preferred_batch: "",
    expected_joining: "",
    internal_notes: "",
    parent_conversation_notes: "",
    fee_discussion_notes: "",
  });
  const [phoneValid, setPhoneValid] = useState(false);
  const [counsellors, setCounsellors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load counsellors + branches once.
  useEffect(() => {
    (async () => {
      try {
        const [b, c] = await Promise.all([
          api.get("/branches"),
          api.get("/users", { params: { role: "counsellor" } }).catch(() => ({ data: [] })),
        ]);
        setBranches(Array.isArray(b.data) ? b.data : (b.data?.items || []));
        const cdata = c.data;
        setCounsellors(
          Array.isArray(cdata) ? cdata
          : Array.isArray(cdata?.items) ? cdata.items
          : Array.isArray(cdata?.users) ? cdata.users
          : []
        );
      } catch (e) { /* non-fatal */ }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.phone || !phoneValid) {
      toast.error("Please enter a valid mobile with country code.");
      return;
    }
    if (!f.branch_id) {
      toast.error("Please pick a branch.");
      return;
    }
    setLoading(true);
    try {
      // Create the enquiry first with the public fields.
      const { data } = await api.post("/enquiries", {
        parent_name: f.parent_name,
        child_name: f.child_name,
        phone: f.phone,
        email: f.email || undefined,
        age_group: f.age_group,
        source: f.source,
        branch_id: f.branch_id,
        interested_in: f.interested_in,
      });
      // Then layer the CRM-only fields via PATCH (admin-only mutations).
      const crm = {};
      if (f.temperature) { crm.temperature = f.temperature; crm.lead_priority = f.temperature; }
      if (f.counsellor_id) crm.counsellor_id = f.counsellor_id;
      if (f.follow_up_date) crm.follow_up_date = f.follow_up_date;
      if (f.admission_probability !== "") crm.admission_probability = parseInt(f.admission_probability, 10);
      if (f.preferred_batch) crm.preferred_batch = f.preferred_batch;
      if (f.expected_joining) crm.expected_joining = f.expected_joining;
      if (f.internal_notes) crm.internal_notes = f.internal_notes;
      if (f.parent_conversation_notes) crm.parent_conversation_notes = f.parent_conversation_notes;
      if (f.fee_discussion_notes) crm.fee_discussion_notes = f.fee_discussion_notes;
      if (Object.keys(crm).length) {
        await api.patch(`/enquiries/${data.enquiry_id}`, crm).catch(() => null);
      }
      toast.success("Enquiry added to your pipeline");
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  // Temperature pill helper.
  const TempPill = ({ v, label, icon: Icon, tone }) => {
    const on = f.temperature === v;
    const cls = on
      ? { hot: "bg-rose-500 text-white border-rose-500",
          warm: "bg-amber-400 text-amber-950 border-amber-400",
          cold: "bg-sky-400 text-sky-950 border-sky-400" }[tone]
      : "bg-paper-alt text-ink-soft border-border hover:bg-paper";
    return (
      <button type="button" onClick={() => setF({ ...f, temperature: v })}
        data-testid={`ne-temp-${v}`}
        className={`px-3 py-1.5 rounded-full text-xs font-semibold border flex items-center gap-1.5 ${cls}`}>
        <Icon weight="duotone" size={14} /> {label}
      </button>
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[92vh] overflow-y-auto bg-white rounded-t-3xl sm:rounded-3xl border border-border p-6"
        data-testid="new-enquiry-modal">
        <div className="flex items-start justify-between mb-1">
          <div>
            <span className="text-[10px] uppercase tracking-wider text-terracotta font-semibold">CRM — Internal Entry</span>
            <h3 className="font-heading text-xl font-bold">Add enquiry</h3>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-full hover:bg-paper-alt" aria-label="Close" data-testid="ne-close-btn">
            <X size={18} />
          </button>
        </div>
        <p className="text-xs text-ink-soft mb-4">
          For phone, walk-in or counsellor entries. Public web/QR enquiries land here automatically.
        </p>

        {/* Family + child basics */}
        <div className="grid sm:grid-cols-2 gap-3">
          <NameInput value={f.parent_name} onChange={(v) => setF({ ...f, parent_name: v })}
            placeholder="Parent name *" testid="ne-parent-name" label="parent name" />
          <NameInput value={f.child_name} onChange={(v) => setF({ ...f, child_name: v })}
            placeholder="Child name *" testid="ne-child-name" label="child name" />
          <PhoneInput value={f.phone} onChange={(v) => setF({ ...f, phone: v })}
            onValidityChange={setPhoneValid} placeholder="Parent mobile (with country code) *"
            testId="ne-phone" />
          <input type="email" placeholder="Parent email" value={f.email}
            onChange={(e) => setF({ ...f, email: e.target.value })}
            className="input" data-testid="ne-email" />
          <select value={f.age_group} onChange={(e) => setF({ ...f, age_group: e.target.value })}
            className="input" data-testid="ne-age-group">
            {["Playgroup", "Nursery", "LKG", "UKG", "Daycare"].map((a) => <option key={a}>{a}</option>)}
          </select>
          <select value={f.interested_in} onChange={(e) => setF({ ...f, interested_in: e.target.value })}
            className="input" data-testid="ne-interested-in">
            <option value="preschool">Preschool only</option>
            <option value="daycare">Daycare only</option>
            <option value="both">Preschool + Daycare</option>
            <option value="activity">Activity-only</option>
            <option value="after_school">After-school care</option>
          </select>
          {(user?.role === "super_admin" || user?.role === "hq_admin"
              || user?.role === "franchise_hq_admin" || (user?.branch_ids?.length > 1)) && (
            <select value={f.branch_id} onChange={(e) => setF({ ...f, branch_id: e.target.value })}
              className="input sm:col-span-2" data-testid="ne-branch">
              <option value="">Select branch *</option>
              {branches.map((b) => (
                <option key={b.branch_id} value={b.branch_id}>{b.name} — {b.city}</option>
              ))}
            </select>
          )}
          <select value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })}
            className="input sm:col-span-2" data-testid="ne-source">
            <optgroup label="Inbound">
              <option>Walk-in</option><option>Phone call</option><option>WhatsApp</option>
              <option>Website</option><option>QR code</option>
            </optgroup>
            <optgroup label="Marketing">
              <option>Google Ads</option><option>Meta Ads</option><option>Social media</option>
              <option>School event</option><option>Campaign</option>
            </optgroup>
            <optgroup label="Network">
              <option>Referral</option><option>Counsellor entry</option><option>Branch admin entry</option>
            </optgroup>
            <option>Other</option>
          </select>
        </div>

        {/* CRM block */}
        <div className="mt-5 pt-4 border-t border-border space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-ink">
            <Briefcase weight="duotone" /> Counsellor & follow-up
          </div>

          <div>
            <div className="text-xs text-ink-soft mb-1.5">Lead priority</div>
            <div className="flex gap-1.5">
              <TempPill v="hot" label="Hot" icon={Fire} tone="hot" />
              <TempPill v="warm" label="Warm" icon={ThermometerHot} tone="warm" />
              <TempPill v="cold" label="Cold" icon={Snowflake} tone="cold" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <select value={f.counsellor_id} onChange={(e) => setF({ ...f, counsellor_id: e.target.value })}
              className="input" data-testid="ne-counsellor">
              <option value="">Assign counsellor (optional)</option>
              {counsellors.map((c) => (
                <option key={c.user_id} value={c.user_id}>{c.name}</option>
              ))}
            </select>
            <input type="date" value={f.follow_up_date}
              onChange={(e) => setF({ ...f, follow_up_date: e.target.value })}
              className="input" data-testid="ne-followup"
              placeholder="Follow-up date" />
            <NumberInput value={f.admission_probability}
              onChange={(v) => setF({ ...f, admission_probability: v })}
              placeholder="Admission probability (%)" min={0} max={100} maxLength={3}
              testid="ne-probability" label="probability" />
            <input value={f.preferred_batch}
              onChange={(e) => setF({ ...f, preferred_batch: e.target.value })}
              placeholder="Preferred batch (e.g. Morning, 9-12)"
              className="input" data-testid="ne-batch" />
            <input type="date" value={f.expected_joining}
              onChange={(e) => setF({ ...f, expected_joining: e.target.value })}
              className="input sm:col-span-2" data-testid="ne-joining"
              placeholder="Expected joining" />
          </div>

          <textarea value={f.parent_conversation_notes}
            onChange={(e) => setF({ ...f, parent_conversation_notes: e.target.value })}
            placeholder="Parent conversation notes — what did they say?"
            rows={2} className="input min-h-[64px]" data-testid="ne-parent-notes" />
          <textarea value={f.fee_discussion_notes}
            onChange={(e) => setF({ ...f, fee_discussion_notes: e.target.value })}
            placeholder="Fee discussion notes — any concessions discussed?"
            rows={2} className="input min-h-[64px]" data-testid="ne-fee-notes" />
          <textarea value={f.internal_notes}
            onChange={(e) => setF({ ...f, internal_notes: e.target.value })}
            placeholder="Internal notes (only your team sees this)"
            rows={2} className="input min-h-[64px]" data-testid="ne-internal-notes" />
        </div>

        <div className="flex gap-2 pt-5">
          <button type="button" onClick={onClose}
            className="flex-1 h-11 rounded-full border border-border" data-testid="ne-cancel">Cancel</button>
          <button disabled={loading}
            className="flex-1 h-11 rounded-full bg-terracotta text-white font-semibold hover:bg-terracotta-hover disabled:opacity-60"
            data-testid="ne-submit">
            {loading ? "Saving…" : "Create enquiry"}
          </button>
        </div>
      </form>
    </div>
  );
}


// Compact follow-up date affordance for the enquiry cards.
function FollowUpHint({ date }) {
  if (!date) return null;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((d - today) / 86400000);
  let label;
  let tone = "text-ink-soft";
  if (days < 0)  { label = `Overdue ${-days}d`; tone = "text-rose-600 font-semibold"; }
  else if (days === 0) { label = "Today"; tone = "text-terracotta font-semibold"; }
  else if (days === 1) { label = "Tomorrow"; tone = "text-mustard-deep font-semibold"; }
  else if (days <= 7) { label = `In ${days}d`; tone = "text-ink"; }
  else { label = d.toLocaleDateString(undefined, { day: "numeric", month: "short" }); }
  return <span className={`text-[10px] ${tone}`}>{label}</span>;
}


// ─── P6E filter helpers ─────────────────────────────────────────────
function FilterInput({ label, value, onChange, type = "text", placeholder, testid, icon }) {
  return (
    <label className="block">
      <span className="text-xs text-ink-soft block mb-1">{label}</span>
      <div className="relative">
        {icon && <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-soft">{icon}</span>}
        <input
          type={type} value={value ?? ""} placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className={`w-full h-10 ${icon ? "pl-8" : "pl-3"} pr-3 rounded-xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-terracotta placeholder:text-ink-soft/60`}
        />
      </div>
    </label>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-xs text-ink-soft block mb-1">{label}</span>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full h-10 px-3 rounded-xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-terracotta">
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}
